import { Injectable, Output, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { environment as env } from '../../environments/environment';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { ToastService } from './toast.service';
import { llog } from '../@shared/helpers';
import { redirectToHostedUi, hostedUiLogoutUrl } from '../auth/oidc';

/**
 * Cookie-based session per AUTH-REDESIGN-directives.md.
 *
 *   - No token in JS state, localStorage, sessionStorage, or any URL.
 *   - canActivate calls GET /rest/api/session — cookie auto-sent by the
 *     browser — and reads the user identity from the response body.
 *   - On 401: try POST /rest/api/refresh; on still-401 → Cognito Hosted UI.
 *   - logout(): clear backend cookies + bounce to Hosted UI /logout.
 *
 * Identity fields cached in-memory so synchronous getters (is_admin(),
 * get_role(), get_customers()) work for downstream code. Cache is
 * invalidated on logout or when /api/session returns a different user.
 */

interface SessionUser {
  sub: string;
  email?: string;
  // Cognito's `name` claim is the GIVEN (first) name; `family_name` is
  // the last. Backend /api/session surfaces both so the shared
  // f2-account-menu component can render "First Last" over email in
  // the coin dropdown. Nulls when the Cognito attrs are empty.
  firstName?: string | null;
  lastName?: string | null;
  username?: string;
  customers: string[];
  scanners: string[];
  role: string | null;
  is_admin: boolean;
  live_data_access: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements CanActivate {

  // Standalone route flag preserved (used by /data-agreements UI to
  // hide the admin chrome when launched as a popup).
  private _standalone_route = true;

  @Output() user_changed = new EventEmitter<SessionUser | null>();
  private _user: SessionUser | null = null;

  // True once we've made our first /api/session call. Guards against
  // races where multiple guards on a single route navigation each call
  // bootstrap concurrently.
  private _bootstrapped = false;
  private _bootstrap_inflight: Promise<SessionUser | null> | null = null;

  constructor(private http: HttpClient,
    private loadingService: LoadingService,
    private router: Router,
    private toast: ToastService) {
  }

  /**
   * Load session from cookie. Returns the user, or null on 401. Caches
   * the user; further callers within the same nav get the cached value.
   */
  async bootstrap(): Promise<SessionUser | null> {
    if (this._bootstrapped) return this._user;
    if (this._bootstrap_inflight) return this._bootstrap_inflight;

    this._bootstrap_inflight = this._loadSession().finally(() => {
      this._bootstrapped = true;
      this._bootstrap_inflight = null;
    });
    return this._bootstrap_inflight;
  }

  private async _loadSession(): Promise<SessionUser | null> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/api/session`, { withCredentials: true })
        .toPromise();
      this._setUser(res?.user || null);
      return this._user;
    } catch (e: any) {
      const status = e?.status;
      if (status === 401) {
        // Access cookie missing/expired — try refresh.
        try {
          const res: any = await this.http
            .post(`${env.apiUrl}/rest/api/refresh`, {}, { withCredentials: true })
            .toPromise();
          this._setUser(res?.user || null);
          return this._user;
        } catch {
          this._setUser(null);
          return null;
        }
      }
      llog('session bootstrap error', e);
      this._setUser(null);
      return null;
    }
  }

  private _setUser(user: SessionUser | null) {
    const changed = JSON.stringify(this._user) !== JSON.stringify(user);
    this._user = user;
    if (changed) this.user_changed.emit(user);
  }

  // Synchronous getters used by templates + guards. Callers MUST have
  // already awaited bootstrap() (or canActivate, which awaits it).
  is_logged_in(): boolean { return !!this._user; }
  is_admin(): boolean { return !!this._user?.is_admin; }
  get_role(): string { return this._user?.role || ''; }
  get_username(): string { return this._user?.username || ''; }
  /**
   * Display-friendly identity for the account chip. Admin's /api/session
   * returns email directly on SessionUser, so no JWT decode needed —
   * unlike per-product scanner SPAs where the same chip falls back to
   * decoding the id_token. Returns '' when missing so the template
   * binds a real string.
   */
  get_email(): string { return this._user?.email || this._user?.username || ''; }
  /**
   * Given / family name accessors. Backend /api/session sends the
   * Cognito `name` (given) + `family_name` (last) claims through as
   * `firstName` / `lastName`. Returns '' when the attr is empty so
   * the shared account-menu template binds a real string. NEVER
   * falls back to username (that's a sub UUID for per-customer pools
   * — feedback_never_display_username_uuid.md).
   */
  get_first_name(): string { return this._user?.firstName || ''; }
  get_last_name(): string { return this._user?.lastName || ''; }
  get_user(): SessionUser | null { return this._user; }
  get_customers(): string[] { return this._user?.customers || []; }
  get_scanners(): string[] { return this._user?.scanners || []; }
  get_live_data_access(): boolean { return !!this._user?.live_data_access; }

  // Kept for downstream callers; routes can carry { data: { standalone: true } }
  get_standalone_route(): boolean { return this._standalone_route; }

  // Backwards-compat shim for legacy templates that called this — admin
  // doesn't show a "stale token" warning anymore (the backend enforces it).
  is_token_stale(): boolean { return !this._user; }

  /**
   * Start the Hosted UI sign-in flow. Redirects out of the app — never
   * returns synchronously. Caller should NOT continue navigation after
   * calling this.
   */
  async login_redirect(returnTo: string = '/home'): Promise<void> {
    await redirectToHostedUi(returnTo);
  }

  /**
   * Clear the session everywhere:
   *   1. Backend /api/logout clears our cookies + returns the Hosted UI
   *      /logout URL.
   *   2. Navigate to that URL so Cognito clears its session cookie too.
   *   3. Cognito redirects to logout_uri (the app's origin).
   *
   * CLIENT-ROLE FALLBACK: if a non-admin user somehow landed on the
   * admin app (misconfigured entitlement, shared URL, cross-app
   * navigation), skip the Hosted UI /logout hop entirely. Their token
   * came from a per-customer Cognito pool that isn't wired for THIS
   * app's PKCE client — the Hosted UI /logout call would 400
   * ("Invalid request") on logout_uri and strand them signed-in.
   * Backend /api/logout still clears our cookies; we hard-nav to the
   * clients app origin instead so they land somewhere they belong.
   * Mike 2026-07-02: "admin app account coin logout doesn't work when
   * a client accidently gets access".
   */
  async logout(): Promise<void> {
    const role = String(this._user?.role || '').toLowerCase();
    const isAdmin = role === 'admin' || !!this._user?.is_admin;
    const logoutUri = `${window.location.origin}/`;
    // Firefox 152 aborts the CORS preflight for our cross-origin POST
    // (admin.f2-tech.ai → f2-admin-service2.f2-tech.ai) — HAR shows
    // status=0/time=0 on both the OPTIONS and the actual POST, while
    // same-page cross-origin GETs continue at 200. Same-site cookies
    // + Access-Control-Allow-* headers are all correct server-side
    // (verified via curl); it's a Firefox 152 CORS quirk on POST.
    //
    // sendBeacon() sidesteps CORS entirely: it fires as a `simple`
    // request (application/x-www-form-urlencoded body → no preflight)
    // and the browser guarantees delivery even if we navigate away
    // immediately after. No response comes back so we compute the
    // Cognito Hosted-UI logout URL client-side. Mike 2026-07-05.
    let sent = false;
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const body = new Blob(
          [`logoutUri=${encodeURIComponent(logoutUri)}`],
          { type: 'application/x-www-form-urlencoded' },
        );
        sent = navigator.sendBeacon(`${env.apiUrl}/rest/api/logout`, body);
      }
    } catch { /* fall through to HttpClient below */ }
    // Fallback: if sendBeacon isn't available OR the browser refused
    // (queue full, offline, etc.), fire-and-forget through HttpClient
    // so the request STILL goes out even if it never completes on the
    // browser side. The .subscribe() below explicitly disables the
    // await so we don't block on Firefox's abort.
    if (!sent) {
      try {
        this.http
          .post(`${env.apiUrl}/rest/api/logout`, { logoutUri }, { withCredentials: true })
          .subscribe({ next: () => {}, error: () => {} });
      } catch { /* ignore */ }
    }
    // Clear local session state + hard-nav. No await on the network
    // call — Cognito refresh revocation is best-effort; the browser
    // is about to lose the cookies anyway.
    this._setUser(null);
    this._bootstrapped = false;
    if (!isAdmin) {
      window.location.assign(this._clientRoleLandingUrl());
      return;
    }
    window.location.assign(hostedUiLogoutUrl(logoutUri));
  }

  /**
   * Where to send a client-role user after logout — the clients app
   * on the same TLD. Falls back to the current origin when we can't
   * resolve a sibling host. Cookies were already cleared by the
   * /api/logout call, so this is just a landing hop.
   */
  private _clientRoleLandingUrl(): string {
    try {
      const host = window.location.hostname.toLowerCase();
      // admin.f2-tech.<tld> → clients.f2-tech.<tld>
      const clientsHost = host.replace(/^admin\./, 'clients.');
      if (clientsHost && clientsHost !== host) {
        return `${window.location.protocol}//${clientsHost}/`;
      }
    } catch { /* ignore */ }
    return `${window.location.origin}/`;
  }

  redirect_to_login(returnTo: string = '/home') {
    // Single-shot: bounce out of the app to Cognito.
    void this.login_redirect(returnTo);
  }

  /**
   * Route guard. Runs before each protected route. Returns false (and
   * starts a Hosted UI redirect) if there's no valid session.
   */
  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    const user = await this.bootstrap();
    if (!user) {
      this.redirect_to_login(state.url || '/home');
      return false;
    }

    const _url = state.url.split('?')[0];

    // Members can only view /data-agreements.
    if (user.role === 'member' && _url !== '/data-agreements') {
      this.router.navigate(['/404'], { skipLocationChange: true });
      return false;
    }

    // Plain client-role users don't have any panel in the admin app —
    // the whole client-slim surface lives in the separate f2-clients
    // SPA. If one lands here (misconfigured entitlement, shared URL,
    // cross-app hop), bounce them out to their real app on the sibling
    // subdomain. /data-agreements is an exception — a client may still
    // need to accept an exchange agreement via the shared popup route.
    // Mike 2026-07-02: remove the legacy /client/users panel + force
    // client-role users out of the admin app entirely.
    if (user.role === 'client' && _url !== '/data-agreements') {
      window.location.assign(this._clientRoleLandingUrl());
      return false;
    }

    // Standalone-route flag for chrome-less embeds.
    const standalone_param = route.queryParams['stroute'];
    const standalone_route = route.data?.['standalone'] === true;
    this._standalone_route = (standalone_param === '1' || standalone_route) ? true : false;

    return true;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AdminAuthGuard implements CanActivate {
  constructor(private authService: AuthService) {}
  async canActivate(route: ActivatedRouteSnapshot) {
    // Angular runs sibling canActivate guards in PARALLEL — the prior
    // "parent has already awaited bootstrap" comment was wrong and
    // caused deep-links like /admin/error-reports/:id to silently
    // bounce when this guard raced bootstrap. Await explicitly; the
    // call is idempotent + inflight-shared so siblings cost nothing.
    await this.authService.bootstrap();
    return this.authService.is_admin();
  }
}

/**
 * Guard for routes that super-admin AND client_admin can access. The
 * backend scopes the response (e.g. users list filtered by
 * custom:customers for client_admin) — the guard just admits both
 * roles to the same page; the page renders what the backend returns.
 *
 * Used by /admin/users so an Option Pit client_admin can manage their
 * own customer's users without seeing other clients' rows. /admin/
 * super-admin-only pages (billing, scanners, pending-deletions) stay
 * on the strict AdminAuthGuard.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminOrClientAdminAuthGuard implements CanActivate {
  constructor(private authService: AuthService) {}
  async canActivate(route: ActivatedRouteSnapshot) {
    // See AdminAuthGuard — sibling guards race. is_admin() returned
    // false while bootstrap was still inflight, which silently bounced
    // every /admin/* deep-link to the home/picker route.
    await this.authService.bootstrap();
    if (this.authService.is_admin()) return true;
    const role = this.authService.get_role();
    return role === 'client_admin';
  }
}
